import builtOnSureLogo from '../../assets/toyota/image/built-on-sure.svg';
import errorImg from '../../assets/toyota/image/errorImg.svg';
import peopleLaughingImgPng from '../../assets/toyota/image/people-laughing.png';
import peopleLaughingImgWebP from '../../assets/toyota/image/people-laughing.webp';
import tenantDesktopBuckleUpImg from '../../assets/toyota/image/toyota-buckle-up-desktop.png';
import tenantMobileBuckleUpImg from '../../assets/toyota/image/toyota-buckle-up-mobile.png';
import { ReactComponent as TenantCarIcon } from '../../assets/toyota/image/toyota-car-icon.svg';
import { ReactComponent as SuccessIcon } from '../../assets/toyota/image/toyota-checkmark.svg';
import { ReactComponent as TenantDriverIcon } from '../../assets/toyota/image/toyota-driver-icon.svg';
import tenantCityImg from '../../assets/toyota/image/toyota-little-city.svg';
import { ReactComponent as ToyotaLogo } from '../../assets/toyota/image/toyota-logo.svg';
import toyotaCoverageImg from '../../assets/toyota/image/toyota-protect-your-coverage.svg';
import { ReactComponent as TenantVehicleIcon } from '../../assets/toyota/image/toyota-vehicle-icon.svg';
import { ReactComponent as TenantAlertIcon } from '../../assets/toyota/image/toyota-yellow-alert.svg';
import KnockoutCopy from '../../components/KnockoutCopy/KnockoutCopy';
import Text from '../../components/Text';
import { prepUrlForEnvironment } from '../../utils';

const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
  shouldUseCurrentHost: (hostname: string) => hostname === 'localhost',
});

const toyotaAutoInsuranceUrl = 'https://toyotaims.com/toyotaautoinsurance';

const config = {
  addonModalConfig: {
    carbonOffsetDescriptionCopy: '',
  },
  buckleUpConfig: {
    tenantDesktopBuckleUpImg,
    tenantMobileBuckleUpImg,
  },
  cmsSlugs: {
    authenticatedFooterSlug: 'toyota-footer',
    copyrightSlug: 'toyota-copyright',
    footerSlug: 'toyota-footer',
    privacyPolicySlug: 'toyota-privacy-policy',
    termsViewSlug: 'toyota-terms-and-conditions',
  },
  coverageConfig: {
    subHeader: `To start, we've made selections based on your current policy. Adjust items to get the exact coverage you want. Or, if everything looks good, just skip to the next step.`,
    tenantAssetsImg: toyotaCoverageImg,
    tenantLoveImg: toyotaCoverageImg,
    tenantVehicleImg: toyotaCoverageImg,
  },
  coverageStartDateConfig: {
    infoCopy: null,
    labelCopy: 'Select a start date',
  },
  cssFiles: ['toyota.css'],
  datePickerConfig: {
    placeholder: 'mm/dd/yyyy',
  },
  discountsConfig: {
    hasCarbonOffsetDiscount: false,
    hasPartnerDiscount: true,
    partnerDiscountDescription:
      'You are eligible for this discount because Toyota Insurance Management Solutions is an affinity partner of Toggle, a member of the Farmers Insurance family and the underwriter of Toyota Auto Insurance.',
  },
  errorPageConfig: {
    backgroundImgSrc: errorImg,
  },
  featureFlags: {
    isAccountManagementEnabled: false,
    isAutoPurchaseFlowEnabled: true,
    isAutoQuoteLinkExchangeEnabled: true,
    isDirectToAutoQuoteEnabled: true,
    isLogoRedirectInAutoPurchaseFlowEnabled: true,
    isPrivacyAndTermsEnabled: true,
    isRentersQuoteCreationEnabled: false,
    isSelfServiceEnabled: false,
  },
  footerConfig: {
    hasNavItems: false,
    hasSocialLinks: false,
    sureFooterLogoSrc: builtOnSureLogo,
    tenantPhoneVariant: 'second-row',
  },
  hiddenCoverages: ['gap', 'loan_lease_payoff'],
  icons: {
    SuccessIcon,
    TenantAlertIcon,
    TenantCarIcon,
    TenantDriverIcon,
    TenantVehicleIcon,
    trustIcons: [
      'pf-visa',
      'pf-mastercard-alt',
      'pf-american-express-alt',
      'pf-discover',
    ],
  },
  iidKnockoutCopy: {
    featureFlag: 'isUpdatedToyotaKnockoutCopy',
    subHeader: <KnockoutCopy phoneNumber="888-492-1780" />,
  },
  layoutContentConfig: {
    tenantCityImg,
  },
  layoutQuoteConfig: {
    isLogoHeader: false,
    summaryPosition: 'sticky',
    variant: '100vw-footer',
  },
  localforageDbName: 'toyota',
  logos: {
    default: ToyotaLogo,
    light: ToyotaLogo,
    title: 'Toyota | Toggle',
  },
  navBarConfig: {
    hasNavBarItems: false,
  },
  noAutoCoverageConfig: {
    ctaSubmitCopy: `Thanks! We’ll be in touch as soon as Toyota Auto Insurance is available in your state.`,
    getNoCoverageCopy: ({ regionName }: { regionName: string }) => (
      <>
        {`Toyota Auto Insurance is not in ${regionName} yet, but we can still help
        you, call `}
        <a href="tel:8772490086">877-249-0086</a>.
      </>
    ),
    secondaryCopy: 'Let me know when Toyota Auto comes my way!',
  },
  notFoundPageConfig: {
    backgroundImgSrc: '',
    ctaHref: 'https://toyotaautoinsurance.com',
  },
  notifyConfig: {
    discountCopy:
      'Congratulations! Being a Toyota customer unlocks a discount and exclusive features.',
  },
  partnerCode: 'toggle_toyota',
  passwordValidator: {
    passingIconType: 'circle-fill',
  },
  primaryVehicleDataSource: 'toyota',
  purchaseFlowConfig: {
    checkoutExistingToggleCustomerAlreadyLoggedInCopy:
      "Looks like you're already a Toggle renters customer. Nice!",
    checkoutExistingToggleCustomerLoginSuccessCopy:
      'Great! As an existing Toggle customer, you may be eligible for an additional discount.',
    checkoutExistingToggleCustomerPrompt: (
      <>
        <Text variant="inherit">Do you have Toggle® renters insurance?</Text>
        <Text variant="inherit">
          (Toyota Auto Insurance is powered by Toggle, a member of the Farmers®
          Insurance family.)
        </Text>
      </>
    ),
    checkoutToggleCustomerDefault: false,
  },
  quoteExpirationCondition: {
    maxElapsedTimeMinutes: 60,
    onConfirmDestination: toyotaAutoInsuranceUrl,
    timestampKey: 'updated_at',
  },
  redirectUrls: {
    indexRedirectUrl: toyotaAutoInsuranceUrl,
    mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account?welcome_modal=toyota`,
    isPartnerRedirectUrlEnabled: true,
  },
  signUpViewConfig: {
    backgroundPicture: {
      alt: 'Young adults laughing',
      sources: [
        {
          src: peopleLaughingImgWebP,
          type: 'image/webp',
        },
        {
          src: peopleLaughingImgPng,
          type: 'image/png',
        },
      ],
      fallbackSrc: peopleLaughingImgPng,
    },
    title: 'Cover Your Stuff | Toyota Insurance',
    header:
      "Protecting your journey, wherever the road takes you. Customize your policy and never pay for coverage you don't want.",
    hero2: "let's go.",
    informationalHeader: 'But first...',
    informationalSubHeader: 'Confirm the details below.',
  },
  stripeElStyles: {
    base: {
      fontFamily: "'Toyota Type', Helvetica, sans-serif",
      fontSize: '14px',
      color: '#696969',
      '::placeholder': {
        color: '#aab1c7',
      },
    },
    fonts: [
      {
        family: 'Toyota Type',
        // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
        src: `url(https://sure-host.s3.amazonaws.com/static/toyota/ToyotaType-Regular.ttf) format('truetype')`,
      },
    ],
    invalid: {
      iconColor: '#e50c22',
      color: '#696969',
    },
  },
  tenantDisplayName: 'Toyota',
  tenantPhoneNumber: '1-888-492-1780',
  vehicleManagementCopyConfig: {
    subheader:
      "We've already included your Toyota. Feel free to add additional vehicles.",
  },
};

export default config;
